import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { errorHandler } from 'api/axios_instance';
import { QueryKey } from 'api/model';
import { syncOllamaModelSpecifications } from 'api/model-specifications/requests';

const OLLAMA_SERVER_MODEL_NAMES_KEY = 'ollama-server-model-names';

const normalizeBaseUrl = (baseUrl: string) => baseUrl.trim().replace(/\/+$/, '');

const useOllamaSyncedModels = (baseUrl?: string) => {
  const queryClient = useQueryClient();
  const normalizedBaseUrl = baseUrl ? normalizeBaseUrl(baseUrl) : '';
  const queryKey = [OLLAMA_SERVER_MODEL_NAMES_KEY, normalizedBaseUrl] as const;

  const { data: serverModelNames = [] } = useQuery<string[]>({
    queryKey,
    queryFn: () => queryClient.getQueryData<string[]>(queryKey) ?? [],
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: normalizedBaseUrl.length > 0,
  });

  const sync = useMutation({
    mutationFn: (url: string) => syncOllamaModelSpecifications(normalizeBaseUrl(url)),
    onSuccess: (specifications, url) => {
      queryClient.setQueryData<string[]>(
        [OLLAMA_SERVER_MODEL_NAMES_KEY, normalizeBaseUrl(url)],
        specifications.map((spec) => spec.name),
      );
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ModelSpecifications] });
      toast.success(
        `Synced ${specifications.length} model${specifications.length === 1 ? '' : 's'} from Ollama server`,
      );
    },
    onError: errorHandler('Failed to sync Ollama models'),
  });

  return { serverModelNames, sync };
};

export default useOllamaSyncedModels;
